// ============================================================================
// RHFormsPrimeiro15_Externo.js
// Questionário público dos "Primeiros 15 dias" reformulado (padrão pontuado).
// O colaborador acessa por link (WhatsApp/e-mail) validado por token. Grava na
// tabela nova via POST /api/forms-rh/primeiro15/public/submit-v2. A estrutura
// (7 seções + tipos) vem de primeiro15-perguntas.js (fonte única, espelha o back).
// ============================================================================

import React, { useState, useEffect, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { SECOES, ESCALA_ROTULO_MIN, ESCALA_ROTULO_MAX } from './primeiro15-perguntas';

const COR = '#e11d48';

// Obrigatórias = tudo menos texto livre (a sugestão é opcional).
const IDS_OBRIGATORIOS = SECOES.flatMap((s) => s.perguntas.filter((p) => p.tipo !== 'texto').map((p) => p.id));
const RESPOSTAS_INICIAIS = SECOES.reduce((acc, s) => { s.perguntas.forEach((p) => { acc[p.id] = ''; }); return acc; }, {});

// Timestamp Brasília no padrão MySQL (YYYY-MM-DD HH:mm:ss).
const timestampBrasilia = () => {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

// Botões 1–5 (escala) e 0–10 (NPS), no estilo "radio escondido + label clicável".
//
// ⚠️ MORA AQUI FORA de propósito. Quando era declarado dentro do componente-pai,
// o React criava uma função NOVA a cada render — e função nova = tipo novo, o
// que faz o React DESMONTAR e remontar toda a árvore. Como o pai re-renderiza a
// cada tecla digitada no campo de texto, todos os radios do questionário eram
// destruídos e recriados letra por letra; no celular essa reconstrução de DOM
// derrubava o foco e RECOLHIA O TECLADO VIRTUAL. No escopo do módulo a
// identidade é estável e o React só atualiza o que realmente mudou.
// Regra geral: componente nunca se define dentro de outro componente.
const BotoesEscala = ({ id, min, max, rotuloMin, rotuloMax, valor, onSelecionar }) => {
  const nums = [];
  for (let n = min; n <= max; n++) nums.push(n);
  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {nums.map((n) => {
          const isSel = valor === String(n);
          return (
            <Fragment key={`${id}-${n}`}>
              <input type="radio" id={`${id}-${n}`} name={id} value={String(n)} checked={isSel} onChange={() => onSelecionar(id, String(n))} style={{ display: 'none' }} />
              <label htmlFor={`${id}-${n}`} style={{
                flex: '1 1 36px', minWidth: '36px', textAlign: 'center', padding: '12px 0', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold',
                backgroundColor: isSel ? COR : '#f1f5f9', color: isSel ? '#fff' : '#475569', border: `1px solid ${isSel ? COR : '#cbd5e1'}`, transition: 'all 0.2s',
              }}>{n}</label>
            </Fragment>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '12px', color: '#94a3b8' }}>
        <span>{min} — {rotuloMin}</span>
        <span>{max} — {rotuloMax}</span>
      </div>
    </div>
  );
};

function Primeiro15_Externo() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showInfo, setShowInfo] = useState(true);

  const [headerData, setHeaderData] = useState({ loja: '', data: '', nome: '', nomeCargo: '', cpf: '', id_funcionario: '' });
  const [respostas, setRespostas] = useState(RESPOSTAS_INICIAIS);

  const setResposta = (id, value) => setRespostas((prev) => ({ ...prev, [id]: value }));

  useEffect(() => {
    const validarLink = async () => {
      try {
        const response = await fetch(`/api/forms-rh/primeiro15/public/validar-link/${token}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Erro ao validar link.');
        setHeaderData({
          loja: data.dados.LOJA, nome: data.dados.NOME, nomeCargo: data.dados.CARGO,
          cpf: data.dados.CPF, id_funcionario: data.dados.ID, data: timestampBrasilia(),
        });
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    validarLink();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (enviando) return;
    if (IDS_OBRIGATORIOS.some((id) => !respostas[id])) {
      alert('Por favor, responda todas as perguntas obrigatórias antes de enviar.');
      return;
    }
    setEnviando(true);

    const payload = {
      token,
      formData: {
        LOJA: headerData.loja,
        DATA: headerData.data,
        NOME_FUNCIONARIO: headerData.nome,
        CPF: headerData.cpf,
        ID_FUNCIONARIO: headerData.id_funcionario,
        NOME_CARGO: headerData.nomeCargo,
        ...respostas, // P_1..P_19
      },
    };

    try {
      const response = await fetch('/api/forms-rh/primeiro15/public/submit-v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(await response.text());
      setSuccess(true);
    } catch (err) {
      alert('Erro: ' + err.message);
    } finally {
      setEnviando(false);
    }
  };

  // --- Estilos ---
  const inputStyle = { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc', color: '#334155', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' };
  const cardStyle = { backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };

  // --- Renderizadores por tipo ---
  const renderPergunta = (p, numero) => {
    // A `nota` é um aviso curto sob o enunciado (ver primeiro15-perguntas.js).
    // No P_22 ela é o que impede o colaborador de ler "prefere" como "posso
    // pedir troca" — por isso fica ACIMA do campo, e não como rodapé da seção:
    // ele precisa ler antes de escolher, não depois.
    const bloco = (conteudo) => (
      <div key={p.id} style={{ marginBottom: '22px' }}>
        <label style={labelStyle}>{numero}. {p.texto}{p.tipo === 'texto' ? ' (opcional)' : ''}</label>
        {p.nota && (
          <p style={{ fontSize: '13px', lineHeight: '1.5', color: '#64748b', margin: '-4px 0 10px 0' }}>
            {p.nota}
          </p>
        )}
        {conteudo}
      </div>
    );

    if (p.tipo === 'simnao') {
      return bloco(
        <select value={respostas[p.id]} onChange={(e) => setResposta(p.id, e.target.value)} style={inputStyle} required>
          <option value="">Selecione...</option>
          <option value="Sim">Sim</option>
          <option value="Não">Não</option>
        </select>
      );
    }
    if (p.tipo === 'selecao') {
      return bloco(
        <select value={respostas[p.id]} onChange={(e) => setResposta(p.id, e.target.value)} style={inputStyle} required>
          <option value="">Selecione...</option>
          {p.opcoes.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (p.tipo === 'escala') {
      return bloco(<BotoesEscala id={p.id} min={1} max={5} rotuloMin={ESCALA_ROTULO_MIN} rotuloMax={ESCALA_ROTULO_MAX} valor={respostas[p.id]} onSelecionar={setResposta} />);
    }
    if (p.tipo === 'nps') {
      return bloco(<BotoesEscala id={p.id} min={0} max={10} rotuloMin="Nada provável" rotuloMax="Muito provável" valor={respostas[p.id]} onSelecionar={setResposta} />);
    }
    // texto
    return bloco(
      <textarea value={respostas[p.id]} onChange={(e) => setResposta(p.id, e.target.value)} style={{ ...inputStyle, resize: 'vertical', minHeight: '100px' }} rows="4" placeholder="Digite aqui..." />
    );
  };

  // --- Telas de estado ---
  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f8fafc', color: '#64748b', fontSize: '18px' }}>Carregando formulário...</div>;
  if (error) return <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#fef2f2', color: '#991b1b', padding: '20px', textAlign: 'center' }}><h2 style={{ fontSize: '24px', marginBottom: '10px' }}>Link Inválido ou Expirado</h2><p>{error}</p></div>;
  if (success) return <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#ecfdf5', color: '#065f46', padding: '20px', textAlign: 'center' }}><h1 style={{ fontSize: '60px', margin: '0 0 10px 0' }}>✅</h1><h2 style={{ fontSize: '24px' }}>Questionário Enviado!</h2><p>Obrigado pelo seu tempo e feedback.</p></div>;

  return (
    <div style={{ backgroundColor: '#f1f5f9', minHeight: '100vh', padding: '40px 20px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ width: '100%', maxWidth: '700px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img alt="Logotipo" src="/icon-logotipo.png" style={{ height: '60px', objectFit: 'contain' }} />
        </div>

        <div style={{ backgroundColor: '#ffebeb', borderLeft: `4px solid ${COR}`, padding: '20px', borderRadius: '8px', marginBottom: '24px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          <div onClick={() => setShowInfo(!showInfo)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#b41409', margin: 0 }}>Meus Primeiros 15 Dias</h2>
            <span style={{ color: '#b40909', fontWeight: 'bold' }}>{showInfo ? '▲' : '▼'}</span>
          </div>
          {showInfo && (
            <div style={{ marginTop: '15px', color: '#780f0f', fontSize: '14px', lineHeight: '1.6' }}>
              <p style={{ margin: '0 0 10px 0' }}>Queremos ouvir a sua opinião sobre os seus primeiros 15 dias conosco.</p>
              <p style={{ margin: 0 }}><strong>Responda com sinceridade.</strong> Suas respostas nos ajudam a melhorar a experiência de quem chega.</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Dados do colaborador (readonly) */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>Seus Dados</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div><label style={labelStyle}>Nome</label><input value={headerData.nome} readOnly style={{ ...inputStyle, backgroundColor: '#e2e8f0', color: '#64748b', cursor: 'not-allowed' }} /></div>
              <div><label style={labelStyle}>Unidade (Loja)</label><input value={headerData.loja} readOnly style={{ ...inputStyle, backgroundColor: '#e2e8f0', color: '#64748b', cursor: 'not-allowed' }} /></div>
              <div><label style={labelStyle}>Data</label><input value={headerData.data} readOnly style={{ ...inputStyle, backgroundColor: '#e2e8f0', color: '#64748b', cursor: 'not-allowed' }} /></div>
            </div>
          </div>

          {/* Seções */}
          {SECOES.map((secao) => (
            <div key={secao.id} style={cardStyle}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                {secao.id}. {secao.titulo}
              </h3>
              {secao.perguntas.map((p, idx) => renderPergunta(p, idx + 1))}
            </div>
          ))}

          <button type="submit" disabled={enviando} style={{ width: '100%', padding: '16px', backgroundColor: enviando ? '#94a3b8' : '#0f172a', color: '#fff', fontSize: '16px', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: enviando ? 'not-allowed' : 'pointer', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            {enviando ? 'Enviando Respostas...' : 'Finalizar Questionário'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Primeiro15_Externo;
